import type { Query, QueryClient, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type {
    AiTransformationStatusUpdateRequest,
    AiTransformationStatusUpdateResponse,
} from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import { unwrapResult } from '../../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { invalidatePositionTransformationLists } from '../../../position-management';
import { strategyKeys } from './query-keys';

export type MoveStrategyItemsInput = {
    /** The page's filters, which the backend's update is scoped by. */
    readonly filters: AiTransformationFilters;
    readonly request: AiTransformationStatusUpdateRequest;
};

const STRATEGY_ROOT = strategyKeys.architecture('')[0];
const REFRESHED_READS: readonly unknown[] = ['column', 'items', 'matrix', 'summary', 'tile-priority', 'tree'];

const isRefreshedRead = (architectureId: string | undefined) => (query: Query) => {
    const [root, queryArchitectureId, read] = query.queryKey;
    return (
        root === STRATEGY_ROOT &&
        (architectureId === undefined || queryArchitectureId === architectureId) &&
        REFRESHED_READS.includes(read)
    );
};

/**
 * Cancels every summary, column, list, tree, matrix and tile priority read of the architecture, or of every
 * architecture when none is given, under any filters, and reads them again, so neither a read started before a move nor
 * a move's own response arriving out of order brings back what was there before. Only the reads on screen load again.
 */
export const refreshStrategyReads = async (queryClient: QueryClient, architectureId?: string): Promise<void> => {
    const predicate = isRefreshedRead(architectureId);
    await queryClient.cancelQueries({ predicate });
    await Promise.all([
        queryClient.invalidateQueries({ predicate }),
        invalidatePositionTransformationLists(queryClient),
    ]);
};

/**
 * Refetches the strategy reads of every architecture, for callers that moved items outside these mutations: a tile
 * moves an item of its own architecture while the dashboard beside it may read it under another.
 */
export const useRefreshStrategyReads = (): (() => Promise<void>) => {
    const queryClient = useQueryClient();
    return useCallback(() => refreshStrategyReads(queryClient), [queryClient]);
};

/**
 * Moves items to a status and fails when the backend skips any of them. Whatever the outcome, it then refreshes every
 * read of the architecture, so the counts, columns and stages always come from the server after the last move.
 */
export const useMoveStrategyItems = (
    architectureId: string,
): UseMutationResult<AiTransformationStatusUpdateResponse, Error, MoveStrategyItemsInput> => {
    const { updateAiTransformationStatuses } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ filters, request }) => {
            const response = await unwrapResult(updateAiTransformationStatuses({ architectureId, filters, request }));
            // The backend skips items it may no longer move, such as one that reached an automatic status meanwhile.
            if (response.updated < request.ids.length) {
                throw new Error(`Moved ${response.updated} of ${request.ids.length} items`);
            }

            return response;
        },
        onSettled: () => refreshStrategyReads(queryClient, architectureId),
    });
};
